import type { KeyboardEventHandler } from "react";
import { useRef, useEffect } from "react";
import pxToRem from "../../../utils/pxToRem";

import { useCalendarContext } from "../../context";
import { NAVIGATION_STATES, MONTHS } from "../../constants";
import {
  parseDate,
  getOffset,
  isDateInInterval,
  isDateValid,
  isArrow,
  getDateParts,
  getFirstSelectedDate,
} from "../../helpers";
import { DateButton } from "../../DateButton/DateButton";

type Props = {
  "data-date": string;
};

export function Year(props: Props) {
  const ref = useRef<HTMLButtonElement>(null);
  const {
    visible,
    focusedDate,
    currentDate,
    selectedDate,
    selectionType,
    calendarType,
    selectionDateType,
    minDate,
    maxDate,
    structuredDisabledMonths,
    onSelectedDateChange,
    onInnerDateChange,
    setCalendarType,
  } = useCalendarContext();

  const { "data-date": date } = props;
  const { day, month, year } = getDateParts(date);
  const originalMinDate = minDate && isDateValid(minDate) && new Date(minDate);
  const originalMaxDate = maxDate && isDateValid(maxDate) && new Date(maxDate);
  const isDisabled =
    (originalMinDate && originalMinDate.getFullYear() > year) ||
    (originalMaxDate && originalMaxDate.getFullYear() < year) ||
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- на всякий случай проверяем
    (structuredDisabledMonths[year] && structuredDisabledMonths[year].length === MONTHS.length);
  const originalCurrentDate = new Date(currentDate);
  const isCurrentYear = originalCurrentDate.getFullYear() === year;

  let startSelected = false;
  let endSelected = false;
  let tabIndex = -1;
  let isInInterval = false;
  if (selectionType === "day" && selectedDate) {
    const isSelected = selectedDate.some((item) => new Date(item).getFullYear() === year);
    if (isSelected) {
      startSelected = true;
      endSelected = true;
    }
  } else if (selectedDate) {
    const startDate = new Date(selectedDate[0]);
    const endDate = new Date(selectedDate[1]);
    startSelected = startDate.getFullYear() === year;
    endSelected = endDate.getFullYear() === year;
    if (
      !(startSelected || endSelected) &&
      selectionType === "interval" &&
      selectedDate.length === 2
    ) {
      isInInterval = isDateInInterval(selectedDate[0], selectedDate[1], date);
    }
  }
  if (isCurrentYear) {
    tabIndex = 0;
  }

  const handleButtonKeydown: KeyboardEventHandler = (event) => {
    const { key } = event;
    if (isArrow(key)) {
      event.preventDefault();
      const neighbouringYear = year + getOffset("years", key);
      const newDate = new Date(neighbouringYear, month, day);
      const newCurrentDate = parseDate(newDate);
      onInnerDateChange({ date: newCurrentDate, updateFocus: true });
    }
  };

  const handleClick = () => {
    if (startSelected || endSelected) {
      if (selectionType === "interval" && endSelected) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- нужно убедиться, что здесь не null | undefined
        onInnerDateChange({ date: selectedDate![1], updateFocus: true });
      } else {
        const firstSelectedDate = getFirstSelectedDate(
          date,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- нужно убедиться, что здесь не null | undefined
          selectedDate!,
          "year"
        );
        onInnerDateChange({ date: firstSelectedDate, updateFocus: true });
      }
    } else {
      onInnerDateChange({ date, updateFocus: true });
    }
    if (selectionDateType === "year") {
      onSelectedDateChange(date);
    } else {
      const previousStateIndex = NAVIGATION_STATES.indexOf(calendarType) - 1;
      setCalendarType(NAVIGATION_STATES[previousStateIndex]);
    }
  };

  useEffect(
    function updateYearFocus() {
      const isCurrentYearAlreadyFocused = ref.current?.isEqualNode(document.activeElement);
      const originalFocusedDate = focusedDate ? new Date(focusedDate) : undefined;
      const isFocusedYear = originalFocusedDate?.getFullYear() === year;
      if (!isCurrentYearAlreadyFocused && isFocusedYear && visible) {
        ref.current?.focus();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- нет уверенности, что предлагаемая линтером зависимость нужна
    [focusedDate, date, visible]
  );

  return (
    <DateButton
      ref={ref}
      disabled={isDisabled}
      selected={startSelected || endSelected}
      tabIndex={tabIndex}
      isInInterval={isInInterval}
      onClick={handleClick}
      onKeyDown={handleButtonKeydown}
      styles={{
        pt: pxToRem(15),
        pb: pxToRem(15),
        fontFeatureSettings: "'tnum'",
      }}
      {...props}
    >
      {year}
    </DateButton>
  );
}
